import {
    SchedulesReportColumn,
    SchedulesReportView,
} from "@common/types/views/schedulesReportView";
import { SCHEDULES_REPORT_DEFAULT_COLUMNS } from "./BuiltInSchedulesReportViews";
import { View, ViewOptions, ViewType } from "@common/types/views/views";
import { getPeriodKey } from "@common/helpers/reports";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    SCHEDULES_ROLLFORWARD_COLUMN_LABELS,
    SCHEDULES_ROLLFORWARD_COLUMNS,
    SCHEDULES_WATERFALL_COLUMN_LABEL,
    SchedulesReportLayout,
    SchedulesReportRows,
    SchedulesRollforwardColumn,
} from "@common/types/reports/schedules-report";
import { ColumnGroupConfig, ViewConfig } from "@lib/views";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { getValueColumnsPerPeriod } from "./schedulesReportViewBuilder";
import {
    SchedulesColumnConfig,
    SchedulesReportViewRow,
} from "./schedulesReportViewTypes";
import {
    makeSchedulesReportTermDateCell,
    SchedulesReportTitleCell,
} from "./SchedulesReportTitleCell";
import { makeSchedulesReportValueCell } from "./SchedulesReportValueCell";
import {
    SUMMARY_BOOKED_GROUP_ID,
    SUMMARY_COLUMN_IDS,
    SUMMARY_RECOGNIZED_GROUP_ID,
    buildWaterfallSummaryColumns,
    buildWaterfallSummaryGroups,
    showsWaterfallSummary,
} from "./schedulesReportSummaryColumns";

const TERM_DATE_COLUMN_SIZE = {
    minSize: 150,
    maxSize: 160,
};

export const SCHEDULES_TITLE_COLUMN_ID = SchedulesReportColumn.TITLE;
const TERM_START_COLUMN_ID = SchedulesReportColumn.TERM_START;
const TERM_END_COLUMN_ID = SchedulesReportColumn.TERM_END;

interface BuildConfigParams {
    periods: ReportPeriodWithLabel[];
    layout: SchedulesReportLayout;
    rows?: SchedulesReportRows;
    numberFormat?: NumberFormatValues;
}

interface PeriodColumnDescriptor {
    label: string;
    column: SchedulesRollforwardColumn | null;
}

function periodColumnDescriptors(
    layout: SchedulesReportLayout,
    numberFormat?: NumberFormatValues,
): PeriodColumnDescriptor[] {
    if (layout === SchedulesReportLayout.rollforward) {
        return SCHEDULES_ROLLFORWARD_COLUMNS.map((column) => ({
            label: formatMoneyColumnHeader(
                SCHEDULES_ROLLFORWARD_COLUMN_LABELS[column],
                numberFormat,
            ),
            column,
        }));
    }
    return [
        {
            label: formatMoneyColumnHeader(
                SCHEDULES_WATERFALL_COLUMN_LABEL,
                numberFormat,
            ),
            column: null,
        },
    ];
}

function periodColumnId(period: ReportPeriodWithLabel, index: number): string {
    return `period-${getPeriodKey(period)}-${index}`;
}

export function buildSchedulesReportConfig({
    periods,
    layout,
    rows,
    numberFormat,
}: BuildConfigParams): ViewConfig<SchedulesReportViewRow, string, never> {
    const columnsPerPeriod = getValueColumnsPerPeriod(layout);
    const hasSummary = showsWaterfallSummary({ layout, rows });
    const descriptors = periodColumnDescriptors(layout, numberFormat);
    const columns: Record<string, SchedulesColumnConfig> = {
        [SCHEDULES_TITLE_COLUMN_ID]: {
            name: "Schedule",
            header: hasSummary ? "Start month" : "",
            ...(hasSummary && { groupId: SUMMARY_BOOKED_GROUP_ID }),
            accessorKey: "title",
            cell: SchedulesReportTitleCell,
            minSize: 280,
            maxSize: 1200,
            immutable: true,
        },
    };
    if (layout === SchedulesReportLayout.rollforward) {
        columns[TERM_START_COLUMN_ID] = {
            name: "Schedule start date",
            header: "Schedule start date",
            accessorKey: "termStart",
            cell: makeSchedulesReportTermDateCell({ field: "termStart" }),
            ...TERM_DATE_COLUMN_SIZE,
        };
        columns[TERM_END_COLUMN_ID] = {
            name: "Schedule end date",
            header: "Schedule end date",
            accessorKey: "termEnd",
            cell: makeSchedulesReportTermDateCell({ field: "termEnd" }),
            ...TERM_DATE_COLUMN_SIZE,
        };
    }

    // The waterfall has one value per period, so the period label is the
    // column header itself instead of a group over a single "Recognized" column.
    const isWaterfall = layout === SchedulesReportLayout.waterfall;
    const groups: Record<string, ColumnGroupConfig> = hasSummary
        ? buildWaterfallSummaryGroups(periods)
        : {};
    periods.forEach((period, periodIndex) => {
        const groupId = `period-${getPeriodKey(period)}`;
        if (!isWaterfall) {
            groups[groupId] = { header: period.label };
        }
        descriptors.forEach(({ label, column }, columnIndex) => {
            columns[periodColumnId(period, columnIndex)] = {
                name: isWaterfall ? period.label : `${period.label} ${label}`,
                header: isWaterfall ? period.label : label,
                hiddenFromSettings: true,
                accessorKey: "values",
                cell: makeSchedulesReportValueCell({
                    period,
                    periodIndex,
                    flatIndex: periodIndex * columnsPerPeriod + columnIndex,
                    layout,
                    column,
                }),
                ...AMOUNT_COLUMN_SIZE,
                ...(isWaterfall
                    ? hasSummary && {
                          groupPath: [SUMMARY_RECOGNIZED_GROUP_ID],
                      }
                    : { groupId }),
            };
        });
    });

    if (hasSummary) {
        Object.assign(
            columns,
            buildWaterfallSummaryColumns({
                periodCount: periods.length,
                numberFormat,
            }),
        );
    }

    return {
        columns,
        groups,
        nesting: {
            enabled: true,
            expandColumn: SCHEDULES_TITLE_COLUMN_ID,
            childrenKey: "children",
        },
    };
}

/**
 * Merges the page-owned fixed columns (title plus optional term dates, as
 * shown or hidden in the column settings) with the per-response period columns.
 */
export function buildSchedulesReportView({
    periods,
    layout,
    rows,
    options,
    columns = SCHEDULES_REPORT_DEFAULT_COLUMNS,
}: Omit<BuildConfigParams, "numberFormat"> & {
    options?: ViewOptions | null;
    columns?: SchedulesReportView["columns"];
}): View {
    const columnCount = getValueColumnsPerPeriod(layout);
    const hasSummary = showsWaterfallSummary({ layout, rows });
    const isColumnShown = (columnId: string) =>
        columns.some((column) => column.id === columnId);
    return {
        type: ViewType.SCHEDULES_REPORT,
        columns: [
            {
                id: SCHEDULES_TITLE_COLUMN_ID,
                options: { pin: "left" as const },
            },
            ...(layout === SchedulesReportLayout.rollforward
                ? [TERM_START_COLUMN_ID, TERM_END_COLUMN_ID]
                      .filter(isColumnShown)
                      .map((id) => ({ id }))
                : []),
            ...(hasSummary
                ? [
                      {
                          id: SUMMARY_COLUMN_IDS.booked,
                          options: { pin: "left" as const },
                      },
                  ]
                : []),
            ...periods.flatMap((period) =>
                Array.from({ length: columnCount }, (_, index) => ({
                    id: periodColumnId(period, index),
                })),
            ),
            ...(hasSummary
                ? [
                      SUMMARY_COLUMN_IDS.recognized,
                      SUMMARY_COLUMN_IDS.remaining,
                  ].map((id) => ({ id, options: { pin: "right" as const } }))
                : []),
        ],
        filters: {},
        options: options ?? undefined,
    };
}
